'use client'

import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import { Bell, X, CheckCheck, MailOpen, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useRouter } from 'next/navigation'
import { useAuth } from '@/providers/auth-provider'
import { useNotificationsRefetch } from '@/app/(frontend)/context/notificationsRefetchContext'
import { useTwoFactorBlocked } from '@/app/(frontend)/hooks/useTwoFactorBlocked'
import { cn } from '@/lib/utils'
import { NotificationAlert } from '@/components/notification-alert'
import moment from 'moment'
import { ScrollArea } from '@radix-ui/react-scroll-area'

/** A single grouped notification as returned by the server */
interface GroupedNotification {
  /** Stable group id: "<notificationType>::<message>" */
  id: string
  title: string
  message: string
  isRead: boolean
  createdAt: string
  route?: string
  /** How many raw notifications were merged into this group */
  count: number
  /** Raw notification ids — used for mark-as-read calls */
  notificationIds: string[]
}

export const UserNotificationComponent = forwardRef<{ openNotifications: () => void }>(
  (_props, ref) => {
    const [notifications, setNotifications] = useState<GroupedNotification[]>([])
    const [totalUnreadCount, setTotalUnreadCount] = useState(0)
    const [isOpen, setIsOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [hasNextPage, setHasNextPage] = useState(false)
    const [currentOffset, setCurrentOffset] = useState(0)
    const [markingAllAsRead, setMarkingAllAsRead] = useState(false)
    const [showUnreadOnly, setShowUnreadOnly] = useState(false)
    const [hasFetchedOnce, setHasFetchedOnce] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const fetchInProgressRef = useRef(false)
    // Track in-flight notification fetches so we can abort them on logout /
    // unmount — otherwise the cookie is cleared while the request is on the
    // wire and we get a 401 that surfaces in error-dialog on /auth/signin
    // (the logout 401 race).
    const abortControllerRef = useRef<AbortController | null>(null)

    const router = useRouter()
    const { user } = useAuth()
    // Impersonation is exempt from the limited-session gate (#3891) — see useTwoFactorBlocked.
    const twoFactorBlocked = useTwoFactorBlocked()
    const { registerRefetch } = useNotificationsRefetch()

    const filteredNotifications = showUnreadOnly
      ? notifications.filter((n) => !n.isRead)
      : notifications

    useImperativeHandle(ref, () => ({
      openNotifications: () => setIsOpen(true),
    }))

    // Register a refetch callback so other parts of the app can trigger a refresh
    useEffect(() => {
      registerRefetch(() => fetchNotifications(false, false))
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [registerRefetch])

    // Initial fetch when the user is available
    useEffect(() => {
      // Cancel + skip on logout/pre-auth OR a 2FA-pending limited session (#3459): the server
      // denies notifications with 403 until 2FA verification completes, so don't fire the fetch
      // (#3566) — it would only surface as a console error on /auth/signin.
      if (!user || twoFactorBlocked) {
        // Logout / pre-auth: cancel any in-flight request so its rejection
        // doesn't surface as a `TypeError: Failed to fetch` on /auth/signin.
        abortControllerRef.current?.abort()
        abortControllerRef.current = null
        return
      }
      fetchNotifications()
      return () => {
        abortControllerRef.current?.abort()
        abortControllerRef.current = null
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, twoFactorBlocked])

    // Re-fetch when the unread filter is toggled (reset to first page)
    useEffect(() => {
      if (hasFetchedOnce && isOpen) {
        fetchNotifications(false, false)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showUnreadOnly])

    // Close dropdown when clicking outside
    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false)
        }
      }
      if (isOpen) document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen])

    const fetchNotifications = async (silent = false, loadMore = false) => {
      if (fetchInProgressRef.current) return

      try {
        fetchInProgressRef.current = true

        if (!silent) {
          if (loadMore) {
            setLoadingMore(true)
          } else {
            setLoading(true)
            setCurrentOffset(0)
          }
        }

        const offset = loadMore ? currentOffset : 0
        // Abort any previous in-flight fetch and start a fresh controller.
        abortControllerRef.current?.abort()
        const controller = new AbortController()
        abortControllerRef.current = controller
        const response = await fetch(
          `/api/company-meeting-notifications/user-notifications?limit=50&offset=${offset}`,
          { signal: controller.signal },
        )

        if (response.ok) {
          const data = await response.json()

          if (loadMore) {
            setNotifications((prev) => [...prev, ...data.notifications])
            setCurrentOffset((prev) => prev + 50)
          } else {
            setNotifications(data.notifications)
            setCurrentOffset(50)
            // totalUnreadCount is only accurate on the first page (offset=0)
            setTotalUnreadCount(data.totalUnreadCount ?? 0)
          }

          setHasNextPage(data.pagination?.hasNextPage ?? false)
          setHasFetchedOnce(true)
        }
      } catch (error) {
        // Issue #3409: silently ignore operational network errors:
        //   - `AbortError` when the user navigates away (e.g. token expiry
        //     redirect to /auth/signin) or logs out while the fetch is in
        //     flight.
        //   - `TypeError: Failed to fetch` for browser-level network blips
        //     (offline, page unload, transient DNS) — these are not bugs
        //     and previously polluted Logtail's `error` channel with a URL
        //     tied to /auth/signin (see the issue's repro pattern).
        // Anything else is a genuine error and we still want it logged.
        const err = error as { name?: string; message?: string } | null
        const isAbort = err?.name === 'AbortError'
        const isNetwork = err instanceof TypeError && err.message === 'Failed to fetch'
        if (!isAbort && !isNetwork) {
          console.error('Failed to fetch notifications:', error)
        }
      } finally {
        if (!silent) {
          setLoading(false)
          setLoadingMore(false)
        }
        fetchInProgressRef.current = false
      }
    }

    // Prefetch on hover so the panel opens instantly
    const handleMouseEnter = () => {
      if (!hasFetchedOnce && !fetchInProgressRef.current) {
        fetchNotifications(true)
      }
    }

    const markAsRead = async (notification: GroupedNotification) => {
      try {
        const responses = await Promise.all(
          notification.notificationIds.map((id) =>
            fetch(`/api/company-meeting-notifications/mark-read/${id}`, { method: 'POST' }),
          ),
        )

        if (responses.every((r) => r.ok)) {
          setNotifications((prev) =>
            prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n)),
          )
          // Decrement the badge count
          setTotalUnreadCount((prev) => Math.max(0, prev - 1))
        }
      } catch (error) {
        console.error('Failed to mark notification as read:', error)
      }
    }

    const markAllAsRead = async () => {
      try {
        setMarkingAllAsRead(true)
        const response = await fetch('/api/company-meeting-notifications/mark-all-read', {
          method: 'POST',
        })

        if (response.ok) {
          setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })))
          setTotalUnreadCount(0)
        }
      } catch (error) {
        console.error('Failed to mark all notifications as read:', error)
      } finally {
        setMarkingAllAsRead(false)
      }
    }

    const handleNotificationClick = async (notification: GroupedNotification) => {
      if (!notification.isRead) {
        await markAsRead(notification)
      }
      if (notification.route) {
        router.push(notification.route)
      }
      setIsOpen(false)
    }

    const formatDate = (dateString: string) => {
      moment.locale('he')
      return moment(dateString).fromNow()
    }

    return (
      <>
        {/* Pass raw notifications for the alert banner — it only needs the unread ones */}
        <NotificationAlert
          onOpenNotificationsAction={() => setIsOpen(true)}
          notifications={notifications}
        />
        <TooltipProvider>
          <div className="relative" ref={dropdownRef}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative"
                  onClick={() => setIsOpen(!isOpen)}
                  onMouseEnter={handleMouseEnter}
                >
                  <Bell className="h-5 w-5" />
                  {totalUnreadCount > 0 && (
                    <Badge
                      variant="destructive"
                      data-testid="notification-badge"
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                    >
                      {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                    </Badge>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>
                  {totalUnreadCount > 0
                    ? `יש לך ${totalUnreadCount} התראות שלא נקראו`
                    : 'אין התראות חדשות'}
                </p>
              </TooltipContent>
            </Tooltip>

            {isOpen && (
              <div className="absolute left-0 top-full mt-2 w-[39rem] bg-background border rounded-lg shadow-lg z-50">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b">
                  <h3 className="font-semibold text-lg text-left">התראות</h3>
                  <div className="flex items-center space-x-3 space-x-reverse justify-start">
                    <Checkbox
                      id="unread-filter"
                      checked={showUnreadOnly}
                      onCheckedChange={(checked) => setShowUnreadOnly(checked === true)}
                    />
                    <label
                      htmlFor="unread-filter"
                      className="text-sm font-medium cursor-pointer mr-4"
                    >
                      הצג לא נקראו
                    </label>
                    {!showUnreadOnly && notifications.length > 0 && (
                      <span className="text-xs text-muted-foreground mr-2">
                        {hasNextPage
                          ? `מציג ${filteredNotifications.length} (יש עוד)`
                          : `מציג ${filteredNotifications.length} התראות`}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {totalUnreadCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={markAllAsRead}
                        disabled={markingAllAsRead}
                        className="text-xs"
                      >
                        <CheckCheck className="h-3 w-3 ml-1" />
                        {markingAllAsRead ? 'מסמן...' : 'סמן הכל כנקרא'}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsOpen(false)}
                      className="h-6 w-6"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Body */}
                <ScrollArea className="max-h-[70vh] overflow-y-auto">
                  {loading && !hasFetchedOnce ? (
                    <div className="p-4 text-right text-muted-foreground">טוען התראות...</div>
                  ) : filteredNotifications.length === 0 ? (
                    <div className="p-8 text-right text-muted-foreground">
                      <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>{showUnreadOnly ? 'כל ההתראות נקראו' : 'אין התראות להציג'}</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {filteredNotifications.map((notification) => (
                        <div
                          key={notification.id}
                          data-testid="notification-item"
                          data-unread={!notification.isRead ? 'true' : undefined}
                          className={cn(
                            'p-4 cursor-pointer transition-colors hover:bg-muted/50 group',
                            !notification.isRead && 'bg-blue-50/50 border-r-2 border-r-blue-500',
                          )}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="flex items-start gap-3" dir="rtl">
                            {/* Unread dot — rightmost in RTL */}
                            {!notification.isRead && (
                              <div className="mt-1.5 h-2 w-2 bg-blue-500 rounded-full flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4
                                  className={cn(
                                    'text-sm font-medium truncate',
                                    !notification.isRead && 'font-semibold',
                                  )}
                                >
                                  {notification.title}
                                  {notification.count > 1 && (
                                    <span className="mr-1 text-xs font-normal text-muted-foreground">
                                      ({notification.count})
                                    </span>
                                  )}
                                </h4>
                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                  {formatDate(notification.createdAt)}
                                </span>
                              </div>
                              <p
                                className="text-sm text-muted-foreground line-clamp-2 mb-2"
                                title={notification.message}
                                dir="rtl"
                              >
                                {notification.message}
                              </p>
                            </div>
                            {/* Mark as read button — visible on hover for unread notifications */}
                            {!notification.isRead && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-white"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      markAsRead(notification)
                                    }}
                                  >
                                    <MailOpen className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  <p>סמן כנקרא</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      ))}

                      {hasNextPage && (
                        <div className="p-4 text-center border-t">
                          <Button
                            variant="ghost"
                            onClick={() => fetchNotifications(false, true)}
                            disabled={loadingMore}
                            className="w-full"
                          >
                            {loadingMore ? (
                              <>
                                <Loader2 className="h-4 w-4 ml-2 animate-spin" />
                                טוען עוד...
                              </>
                            ) : (
                              'ראה עוד התראות'
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </ScrollArea>
              </div>
            )}
          </div>
        </TooltipProvider>
      </>
    )
  },
)

UserNotificationComponent.displayName = 'UserNotificationComponent'
